/*
 * Session-scoped analytics state for the widget.
 *
 * Holds the single `widgetSessionId` that stitches one visit together and the
 * funnel cursor (active module + last step), and exposes typed actions that build
 * each event's detail payload and dual-emit it via `constellTrack`. No PII is stored
 * or emitted — only structural/funnel data.
 */
import { constellTrack, generateWidgetSessionId } from '@/helpers/tracking';
import { TRACKING_EVENTS } from '@/constants/tracking';
import {
    type WidgetModuleSlugTyping,
    type WidgetRequestSubmittedInputTyping,
    type WidgetStepDescriptorTyping,
} from '@shared/typings';
import { type Ref } from 'vue';

// eslint-disable-next-line max-lines-per-function
export const useWidgetTrackingStore = defineStore('tracking', () => {
    const { venueId } = storeToRefs(useWidgetVenueStore());

    // State
    const widgetSessionId: Ref<string> = ref('');
    const activeModule: Ref<WidgetModuleSlugTyping | null> = ref(null);
    const lastStepId: Ref<string | null> = ref(null);
    const lastStepIndex: Ref<number> = ref(0);
    const completed: Ref<boolean> = ref(false);

    // Common fields present on every payload.
    const baseDetail = (): { widgetSessionId: string, venue: string } => ({
        widgetSessionId: widgetSessionId.value,
        venue: venueId.value,
    });

    // Fired in the widget open handler. Idempotent: a session that is already open
    // is not restarted, so re-entrant open calls don't emit duplicate opens.
    const startSession = (): void => {
        if (widgetSessionId.value) return;

        widgetSessionId.value = generateWidgetSessionId();
        activeModule.value = null;
        lastStepId.value = null;
        lastStepIndex.value = 0;
        completed.value = false;

        constellTrack(TRACKING_EVENTS.WIDGET_OPENED, baseDetail());
    };

    // Fired when a visitor commits to one of the modules. Guards against duplicates
    // when the same module re-triggers (e.g. a persistent view).
    const trackModuleStarted = (module: WidgetModuleSlugTyping): void => {
        if (!widgetSessionId.value || activeModule.value === module) return;

        activeModule.value = module;
        constellTrack(TRACKING_EVENTS.MODULE_STARTED, {
            ...baseDetail(),
            module,
        });
    };

    // Fired on each user-facing step render. The single most important event for drop-off.
    const trackStepViewed = (step: WidgetStepDescriptorTyping): void => {
        if (!widgetSessionId.value) return;

        lastStepId.value = step.stepId;
        lastStepIndex.value = step.stepIndex;
        constellTrack(TRACKING_EVENTS.STEP_VIEWED, {
            ...baseDetail(),
            module: activeModule.value,
            stepId: step.stepId,
            stepIndex: step.stepIndex,
            stepName: step.stepName,
            totalSteps: step.totalSteps,
        });
    };

    // Fired only after the backend confirms a submission (HTTP 2xx). Exactly once per submission.
    const trackRequestSubmitted = (payload: WidgetRequestSubmittedInputTyping): void => {
        if (!widgetSessionId.value) return;

        activeModule.value = payload.module;
        completed.value = true;
        constellTrack(TRACKING_EVENTS.REQUEST_SUBMITTED, {
            ...baseDetail(),
            module: payload.module,
            requestId: payload.requestId,
            eventType: payload.eventType,
            guestAmount: payload.guestAmount,
            budget: payload.budget,
        });
    };

    // Fired in the close handler. `completed` reflects whether the visitor reached a submission.
    const endSession = (): void => {
        if (!widgetSessionId.value) return;

        constellTrack(TRACKING_EVENTS.WIDGET_CLOSED, {
            ...baseDetail(),
            module: activeModule.value,
            lastStepId: lastStepId.value,
            lastStepIndex: lastStepIndex.value,
            completed: completed.value,
        });

        // Clear the id so the next open starts a fresh session.
        widgetSessionId.value = '';
        activeModule.value = null;
        lastStepId.value = null;
        lastStepIndex.value = 0;
        completed.value = false;
    };

    return {
        // State
        widgetSessionId,
        activeModule,
        lastStepId,
        lastStepIndex,
        completed,

        // Actions
        startSession,
        trackModuleStarted,
        trackStepViewed,
        trackRequestSubmitted,
        endSession,
    };
});
